const UNIT_LABELS = {
  cm: 'CM',
  centimeter: 'CM',
  centimeters: 'CM',
  in: 'IN',
  inch: 'IN',
  inches: 'IN',
  intl: "INT'L",
  international: "INT'L",
};

class ProductSizingChart extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    const data = this.#readData();
    const textContent = this.#normalizeText(data);
    if (textContent) {
      this.#renderText(textContent);
      this.dataset.ready = 'true';
      return;
    }

    const charts = this.#normalize(data);

    if (!charts.length) {
      this.innerHTML = '<p class="product-sizing-chart__empty">Sizing information is unavailable.</p>';
      this.dataset.ready = 'true';
      return;
    }

    this.#render(charts);
    this.dataset.ready = 'true';
  }

  #readData() {
    const script = this.querySelector('script[data-sizing-chart-json]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  #normalizeText(data) {
    if (typeof data === 'string') {
      return { paragraphs: this.#splitText(data), items: [] };
    }

    if (Array.isArray(data) && data.every((item) => typeof item !== 'object')) {
      return { paragraphs: data.map((item) => this.#formatValue(item)).filter(Boolean), items: [] };
    }

    if (!data || Array.isArray(data) || typeof data !== 'object') return null;

    const mode = this.#slug(data.display || data.type || data.format || data.layout || '');
    const isTextMode = ['text', 'copy', 'note', 'notes'].includes(mode);
    const text = data.text || data.copy || data.content || data.body || data.description || data.note;
    const paragraphs = text ? this.#splitText(text) : [];
    const items = Array.isArray(data.items) ? data.items.map((item) => this.#formatValue(item)).filter(Boolean) : [];

    if (!paragraphs.length && !items.length) return null;
    if (!isTextMode && this.#looksLikeChartData(data)) return null;

    return { paragraphs, items };
  }

  #looksLikeChartData(data) {
    if (this.#readColumns(data) || this.#readRows(data) || data.units || data.tables || data.charts) return true;
    return Object.keys(data).some((key) => Boolean(this.#unitLabel(key)));
  }

  #splitText(value) {
    if (Array.isArray(value)) return value.map((item) => this.#formatValue(item)).filter(Boolean);
    return this.#formatValue(value)
      .split(/\n{2,}|\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  #normalize(data) {
    if (!data) return [];

    const charts = [];
    const rootColumns = this.#readColumns(data);
    const rootRows = this.#readRows(data);

    if (Array.isArray(data)) {
      const chart = this.#normalizeChart(data, rootColumns, 'cm');
      return chart ? this.#withConvertedInches([chart]) : [];
    }

    if (rootRows && !Array.isArray(rootRows)) {
      for (const [unit, rows] of Object.entries(rootRows)) {
        const chart = this.#normalizeChart(rows, rootColumns, unit);
        if (chart) charts.push(chart);
      }
    }

    const unitSources = [data.units, data.tables, data.charts, data];
    for (const source of unitSources) {
      if (!source || Array.isArray(source) || typeof source !== 'object') continue;

      for (const [unit, value] of Object.entries(source)) {
        if (!this.#unitLabel(unit) || charts.some((chart) => chart.unit === this.#normalizeUnit(unit))) continue;

        const chart = this.#normalizeChart(value, rootColumns, unit);
        if (chart) charts.push(chart);
      }
    }

    if (!charts.length && Array.isArray(rootRows)) {
      const chart = this.#normalizeChart({ columns: rootColumns, rows: rootRows }, rootColumns, data.default_unit || 'cm');
      if (chart) charts.push(chart);
    }

    return this.#withConvertedInches(charts);
  }

  #normalizeChart(value, fallbackColumns, unit) {
    const rows = this.#readRows(value) || (Array.isArray(value) ? value : null);
    if (!Array.isArray(rows) || !rows.length) return null;

    const columns = this.#normalizeColumns(this.#readColumns(value) || fallbackColumns, rows);
    if (!columns.length) return null;

    return {
      unit: this.#normalizeUnit(unit),
      label: this.#unitLabel(unit) || this.#humanize(unit),
      columns,
      rows: rows.map((row) => this.#normalizeRow(row, columns)),
    };
  }

  #readColumns(value) {
    if (!value || Array.isArray(value) || typeof value !== 'object') return null;
    return value.columns || value.headers || value.headings || null;
  }

  #readRows(value) {
    if (!value || typeof value !== 'object') return null;
    if (Array.isArray(value)) return value;
    return value.rows || value.sizes || value.measurements || value.data || value.values || null;
  }

  #normalizeColumns(columns, rows) {
    if (Array.isArray(columns) && columns.length) {
      return columns.map((column, index) => {
        if (typeof column === 'object' && column !== null) {
          const key = column.key || column.id || column.name || column.label || String(index);
          return {
            key: this.#slug(key),
            label: column.label || column.heading || column.name || this.#humanize(key),
            index,
          };
        }

        return {
          key: this.#slug(column),
          label: String(column),
          index,
        };
      });
    }

    const firstObjectRow = rows.find((row) => row && typeof row === 'object' && !Array.isArray(row));
    if (firstObjectRow) {
      return Object.keys(firstObjectRow).map((key, index) => ({
        key: this.#slug(key),
        label: this.#humanize(key),
        index,
      }));
    }

    const longestRow = rows.reduce((length, row) => Math.max(length, Array.isArray(row) ? row.length : 0), 0);
    return Array.from({ length: longestRow }, (_, index) => ({
      key: String(index),
      label: index === 0 ? 'Size' : `Measurement ${index}`,
      index,
    }));
  }

  #normalizeRow(row, columns) {
    if (Array.isArray(row)) return columns.map((column) => this.#formatValue(row[column.index]));
    if (!row || typeof row !== 'object') return columns.map(() => '');

    return columns.map((column) => {
      const value = this.#valueForColumn(row, column);
      return this.#formatValue(value);
    });
  }

  #valueForColumn(row, column) {
    if (column.key in row) return row[column.key];

    const match = Object.entries(row).find(([key]) => this.#slug(key) === column.key);
    return match ? match[1] : '';
  }

  #withConvertedInches(charts) {
    const hasCm = charts.some((chart) => chart.unit === 'cm');
    const hasInches = charts.some((chart) => chart.unit === 'in');
    if (!hasCm || hasInches) return charts;

    const cmChart = charts.find((chart) => chart.unit === 'cm');
    const inchesChart = {
      ...cmChart,
      unit: 'in',
      label: 'IN',
      rows: cmChart.rows.map((row) =>
        row.map((value, index) => {
          const column = cmChart.columns[index];
          if (this.#isSizeColumn(column) || value === '') return value;

          const number = Number(String(value).replace(/[^\d.-]/g, ''));
          return Number.isFinite(number) ? (number / 2.54).toFixed(2) : value;
        })
      ),
    };

    const insertIndex = charts.findIndex((chart) => chart.unit === 'cm') + 1;
    return [...charts.slice(0, insertIndex), inchesChart, ...charts.slice(insertIndex)];
  }

  #render(charts) {
    const id = `sizing-chart-${Math.random().toString(36).slice(2)}`;
    const initialIndex = Math.max(
      0,
      charts.findIndex((chart) => chart.unit === 'cm')
    );

    this.replaceChildren();

    if (charts.length > 1) {
      const tabs = document.createElement('div');
      tabs.className = 'product-sizing-chart__tabs';
      tabs.setAttribute('role', 'tablist');

      charts.forEach((chart, index) => {
        const button = document.createElement('button');
        button.className = 'product-sizing-chart__tab';
        button.type = 'button';
        button.id = `${id}-tab-${index}`;
        button.textContent = chart.label;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', `${id}-panel-${index}`);
        button.setAttribute('aria-selected', String(index === initialIndex));
        button.addEventListener('click', () => this.#select(index));
        tabs.append(button);
      });

      this.append(tabs);
    }

    charts.forEach((chart, index) => {
      const panel = document.createElement('div');
      panel.className = 'product-sizing-chart__panel';
      panel.id = `${id}-panel-${index}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `${id}-tab-${index}`);
      panel.hidden = index !== initialIndex;

      const wrap = document.createElement('div');
      wrap.className = 'product-sizing-chart__table-wrap';

      const table = document.createElement('table');
      table.className = 'product-sizing-chart__table';
      table.append(this.#renderHead(chart.columns), this.#renderBody(chart.rows));

      wrap.append(table);
      panel.append(wrap);
      this.append(panel);
    });
  }

  #renderText(content) {
    this.replaceChildren();

    const wrap = document.createElement('div');
    wrap.className = 'product-sizing-chart__text';

    content.paragraphs.forEach((paragraph) => {
      const element = document.createElement('p');
      element.textContent = paragraph;
      wrap.append(element);
    });

    if (content.items.length) {
      const list = document.createElement('ul');
      content.items.forEach((item) => {
        const element = document.createElement('li');
        element.textContent = item;
        list.append(element);
      });
      wrap.append(list);
    }

    this.append(wrap);
  }

  #renderHead(columns) {
    const thead = document.createElement('thead');
    const row = document.createElement('tr');

    columns.forEach((column) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = column.label;
      row.append(cell);
    });

    thead.append(row);
    return thead;
  }

  #renderBody(rows) {
    const tbody = document.createElement('tbody');

    rows.forEach((row) => {
      const tr = document.createElement('tr');
      row.forEach((value) => {
        const cell = document.createElement('td');
        cell.textContent = value;
        tr.append(cell);
      });
      tbody.append(tr);
    });

    return tbody;
  }

  #select(index) {
    this.querySelectorAll('.product-sizing-chart__tab').forEach((tab, tabIndex) => {
      tab.setAttribute('aria-selected', String(tabIndex === index));
    });

    this.querySelectorAll('.product-sizing-chart__panel').forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }

  #formatValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
    return String(value);
  }

  #isSizeColumn(column) {
    return ['size', 'sizes', 'label', 'name'].includes(column.key);
  }

  #normalizeUnit(unit) {
    const slug = this.#slug(unit);
    if (['centimeter', 'centimeters'].includes(slug)) return 'cm';
    if (['inch', 'inches'].includes(slug)) return 'in';
    if (slug === 'international') return 'intl';
    return slug;
  }

  #unitLabel(unit) {
    return UNIT_LABELS[this.#normalizeUnit(unit)] || '';
  }

  #humanize(value) {
    return String(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  #slug(value) {
    return String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }
}

if (!customElements.get('product-sizing-chart')) {
  customElements.define('product-sizing-chart', ProductSizingChart);
}
